/* eslint-disable max-lines */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Skeleton,
  Autocomplete,
  FormControlLabel,
  Switch,
  Divider,
  ListItemText,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Badge,
  Collapse,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Slide,
  Zoom,
  Fade,
  LinearProgress,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  AttachFile as AttachFileIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  Code,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Undo,
  Redo,
  Highlight as HighlightIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  CloudUpload as UploadIcon,
  Mic as MicIcon,
  NoteAdd as NoteAddIcon,
  Share as ShareIcon,
  TuneRounded as TuneIcon,
  AutoAwesome as AutoAwesomeIcon,
  DriveFileRenameOutline as WriteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import TypographyExtension from '@tiptap/extension-typography';
import Placeholder from '@tiptap/extension-placeholder';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import AudioRecorderButton from '../AudioRecorder/AudioRecorderButton';
import FileUploadButton from './FileUploadButton';
import AttachmentsList from './AttachmentsList';
import NoteAudioPlayer from './NoteAudioPlayer';
import './NotebookEditor.css';

// TipTap Editor Component
// eslint-disable-next-line max-lines-per-function
const NotebookEditor = ({ content, onChange, editable = true, placeholder = 'Start writing your note...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight,
      TypographyExtension,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editable, editor]);

  if (!editor) {
    return null;
  }

  return (
    <Box className="notebook-editor">
      {editable && (
        <Paper elevation={0} sx={{ mb: 2, p: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {/* Text Formatting Group */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBold().run()}
                color={editor.isActive('bold') ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive('bold') ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive('bold') ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatBold fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                color={editor.isActive('italic') ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive('italic') ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive('italic') ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatItalic fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                color={editor.isActive('underline') ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive('underline') ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive('underline') ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatUnderlined fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                color={editor.isActive('strike') ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive('strike') ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive('strike') ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatStrikethrough fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleHighlight().run()}
                color={editor.isActive('highlight') ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive('highlight') ? 'warning.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive('highlight') ? 'warning.light' : 'action.hover' }
                }}
              >
                <HighlightIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Alignment Group */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive({ textAlign: 'left' }) ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive({ textAlign: 'left' }) ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatAlignLeft fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive({ textAlign: 'center' }) ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive({ textAlign: 'center' }) ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatAlignCenter fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive({ textAlign: 'right' }) ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive({ textAlign: 'right' }) ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatAlignRight fontSize="small" />
              </IconButton>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Lists Group */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                color={editor.isActive('bulletList') ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive('bulletList') ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive('bulletList') ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatListBulleted fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                color={editor.isActive('orderedList') ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive('orderedList') ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive('orderedList') ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatListNumbered fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                color={editor.isActive('blockquote') ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive('blockquote') ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive('blockquote') ? 'primary.light' : 'action.hover' }
                }}
              >
                <FormatQuote fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                color={editor.isActive('codeBlock') ? 'primary' : 'default'}
                sx={{
                  bgcolor: editor.isActive('codeBlock') ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: editor.isActive('codeBlock') ? 'primary.light' : 'action.hover' }
                }}
              >
                <Code fontSize="small" />
              </IconButton>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Undo/Redo Group */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
              >
                <Undo fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
              >
                <Redo fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      )}
      <Box sx={{
        border: editable ? '1px solid' : 'none',
        borderColor: 'divider',
        borderRadius: 1,
        p: editable ? 2 : 0,
        minHeight: editable ? 200 : 'auto',
        '& .ProseMirror': {
          minHeight: editable ? '150px' : 'auto',
          outline: 'none',
        },
        '& .ProseMirror p.is-editor-empty:first-child::before': {
          color: 'text.disabled',
          content: 'attr(data-placeholder)',
          float: 'left',
          height: 0,
          pointerEvents: 'none',
        },
      }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

// Helper function to parse dates from various formats
const parseDate = (dateValue) => {
  if (!dateValue) return new Date();

  // Handle Firestore Timestamp objects (from direct Firestore queries)
  if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
    return dateValue.toDate();
  }

  // Handle serialized Firestore Timestamp (from JSON API responses)
  if (dateValue?._seconds !== undefined) {
    return new Date(dateValue._seconds * 1000);
  }

  // Handle ISO date strings
  if (typeof dateValue === 'string') {
    return new Date(dateValue);
  }

  // Handle Date objects
  if (dateValue instanceof Date) {
    return dateValue;
  }

  // Fallback to current date
  return new Date();
};

// Helper function to extract plain text from HTML for preview
const getPlainTextFromHTML = (html) => {
  if (!html) return '';

  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Get text content, which strips all HTML tags
  const text = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up extra whitespace
  return text.replace(/\s+/g, ' ').trim();
};

// Mobile Swipeable Note Card Component - World-class design
 
 
 
 
 
 
 
 
// eslint-disable-next-line max-lines-per-function
const MobileNoteCard = ({ note, onView, onEdit, onDelete, canEdit, isSystemAdmin, index }) => {
  const theme = useTheme();
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const startXRef = useRef(0);
  const cardRef = useRef(null);

  const SWIPE_THRESHOLD = 80;
  const ACTION_WIDTH = 80;

  const getVisibilityColor = () => {
    switch (note.visibility) {
      case 'private':
        return theme.palette.secondary.main;
      case 'public':
        return '#23A38F'; // Teal accent
      case 'role-based':
        return '#D8A200'; // Gold accent
      default:
        return theme.palette.grey[400];
    }
  };

  // Get title - prefer explicit title, then first tag, then fallback
  const noteTitle = note.title
    || (note.tags && note.tags.length > 0
      ? note.tags[0].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : 'Untitled Note');

  // Get preview text - handle audio notes which may have transcription or description
  const contentText = note.content || note.transcription || note.description || '';
  const previewText = getPlainTextFromHTML(contentText);
  const truncatedPreview = previewText.length > 100
    ? previewText.substring(0, 100) + '...'
    : (previewText || 'No content');

  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
    setIsSwiping(true);
    setIsPressed(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - startXRef.current;
    // Allow left swipe (negative) up to threshold, and slight right swipe to reset
    const newSwipeX = Math.max(-ACTION_WIDTH * 2, Math.min(20, diff));
    setSwipeX(newSwipeX);
    setIsPressed(false);
  };

  const handleTouchEnd = () => {
    setIsSwiping(false);
    setIsPressed(false);
    // Snap to action position or back to start
    if (swipeX < -SWIPE_THRESHOLD) {
      setSwipeX(-ACTION_WIDTH * 2);
    } else {
      setSwipeX(0);
    }
  };

  const handleCardClick = () => {
    if (Math.abs(swipeX) < 10) {
      onView(note);
    } else {
      setSwipeX(0);
    }
  };

  const formatRelativeDate = (date) => {
    const now = new Date();
    const noteDate = parseDate(date);
    const diffDays = Math.floor((now - noteDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return format(noteDate, 'EEEE');
    return format(noteDate, 'MMM d');
  };

  return (
    <Fade in timeout={300} style={{ transitionDelay: `${index * 50}ms` }}>
      <Box
        sx={{
          position: 'relative',
          mb: 1.5,
          overflow: 'hidden',
          borderRadius: 4,
        }}
      >
        {/* Swipe action buttons (revealed on swipe) */}
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'stretch',
            zIndex: 0,
            borderRadius: '0 16px 16px 0',
            overflow: 'hidden',
          }}
        >
          {canEdit && (
            <Box
              onClick={() => { onEdit(note); setSwipeX(0); }}
              sx={{
                width: ACTION_WIDTH,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.palette.primary.main,
                cursor: 'pointer',
                transition: 'background 0.2s',
                '&:active': { bgcolor: theme.palette.primary.dark },
              }}
            >
              <EditIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
          )}
          {canEdit && (isSystemAdmin || !note.isImmutable) && (
            <Box
              onClick={() => { onDelete(note); setSwipeX(0); }}
              sx={{
                width: ACTION_WIDTH,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.palette.secondary.main,
                cursor: 'pointer',
                transition: 'background 0.2s',
                '&:active': { bgcolor: theme.palette.secondary.dark },
              }}
            >
              <DeleteIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
          )}
        </Box>

        {/* Main card content */}
        <Box
          ref={cardRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={handleCardClick}
          sx={{
            position: 'relative',
            zIndex: 1,
            transform: isPressed && Math.abs(swipeX) < 10
              ? `translateX(${swipeX}px) scale(0.98)`
              : `translateX(${swipeX}px)`,
            transition: isSwiping ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            bgcolor: 'background.paper',
            borderRadius: 4,
            boxShadow: isPressed
              ? '0 8px 24px rgba(0,0,0,0.15)'
              : '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.5),
            overflow: 'hidden',
            cursor: 'pointer',
            willChange: 'transform',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <Box sx={{ p: 2, pb: 1.5 }}>
            {/* Top row: Date + Visibility */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {formatRelativeDate(note.createdAt)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {note.attachmentCount > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <AttachFileIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                      {note.attachmentCount}
                    </Typography>
                  </Box>
                )}
                {note.isImmutable && (
                  <LockIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                )}
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: getVisibilityColor(),
                    boxShadow: `0 0 0 2px ${alpha(getVisibilityColor(), 0.2)}`,
                  }}
                />
              </Box>
            </Box>

            {/* Title */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                lineHeight: 1.3,
                color: 'text.primary',
                fontSize: '1rem',
              }}
            >
              {noteTitle}
            </Typography>

            {/* Preview text */}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: '0.875rem',
                mb: 1.5,
              }}
            >
              {truncatedPreview || 'No content'}
            </Typography>

            {/* Tags row */}
            {note.tags && note.tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {note.tags.slice(0, 3).map((tag, idx) => (
                  <Chip
                    key={idx}
                    label={tag}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                      border: 'none',
                      '& .MuiChip-label': { px: 1.25 },
                    }}
                  />
                ))}
                {note.tags.length > 3 && (
                  <Chip
                    label={`+${note.tags.length - 3}`}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      bgcolor: alpha(theme.palette.grey[500], 0.1),
                      color: 'text.secondary',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

// Desktop Note Card Component
// eslint-disable-next-line max-lines-per-function
const DesktopNoteCard = ({ note, onView, onEdit, onDelete, onPermissionChange, canEdit, isSystemAdmin }) => {
  const theme = useTheme();
  const [showActions, setShowActions] = useState(false);

  const getVisibilityColor = () => {
    switch (note.visibility) {
      case 'private':
        return theme.palette.secondary.main;
      case 'public':
        return '#23A38F';
      case 'role-based':
        return '#D8A200';
      default:
        return theme.palette.grey[400];
    }
  };

  // Get title - prefer explicit title, then first tag, then fallback
  const noteTitle = note.title
    || (note.tags && note.tags.length > 0
      ? note.tags[0].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : 'Untitled Note');

  // Get preview text - handle audio notes which may have transcription or description
  const contentText = note.content || note.transcription || note.description || '';
  const previewText = getPlainTextFromHTML(contentText);
  const truncatedPreview = previewText.length > 120
    ? previewText.substring(0, 120) + '...'
    : (previewText || 'No content');

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.5),
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: getVisibilityColor(),
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {note.visibility}
            </Typography>
            {note.isImmutable && <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
            {note.attachmentCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <AttachFileIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{note.attachmentCount}</Typography>
              </Box>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setShowActions(!showActions); }}
            sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75, lineHeight: 1.3 }}>
          {noteTitle}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {truncatedPreview || 'No content'}
        </Typography>

        {note.tags && note.tags.length > 1 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
            {note.tags.slice(1, 4).map((tag, index) => (
              <Chip key={index} label={tag} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'grey.100', color: 'text.secondary' }} />
            ))}
            {note.tags.length > 4 && (
              <Chip label={`+${note.tags.length - 4}`} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'grey.200', color: 'text.secondary' }} />
            )}
          </Box>
        )}

        <Typography variant="caption" color="text.disabled">
          {format(parseDate(note.createdAt), 'MMM d, yyyy')}
        </Typography>
      </CardContent>

      <Collapse in={showActions}>
        <Divider />
        <CardActions sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
          <Button size="small" onClick={(e) => { e.stopPropagation(); onView(note); }}>View</Button>
          {canEdit && <Button size="small" onClick={(e) => { e.stopPropagation(); onEdit(note); }}>Edit</Button>}
          {canEdit && (isSystemAdmin || !note.isImmutable) && (
            <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(note); }}>Delete</Button>
          )}
          <Button size="small" onClick={(e) => { e.stopPropagation(); onPermissionChange(note); }}>Permissions</Button>
        </CardActions>
      </Collapse>
    </Card>
  );
};

// Empty State Component
const EmptyState = ({ hasFilters, onCreateNote, isMobile }) => {
  const theme = useTheme();

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          textAlign: 'center',
          py: isMobile ? 8 : 10,
          px: 4,
        }}
      >
        {/* Illustration */}
        <Box
          sx={{
            width: isMobile ? 120 : 140,
            height: isMobile ? 120 : 140,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 4,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.05)', opacity: 0.7 },
              },
            }}
          />
          {hasFilters ? (
            <SearchIcon sx={{ fontSize: isMobile ? 48 : 56, color: alpha(theme.palette.primary.main, 0.4) }} />
          ) : (
            <WriteIcon sx={{ fontSize: isMobile ? 48 : 56, color: alpha(theme.palette.primary.main, 0.4) }} />
          )}
        </Box>

        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1.5,
          }}
        >
          {hasFilters ? 'No matching notes' : 'Start your notebook'}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            maxWidth: 280,
            mx: 'auto',
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {hasFilters
            ? 'Try adjusting your search or filters to find what you are looking for.'
            : 'Capture ideas, meeting notes, and important information. Your first note is just a tap away.'}
        </Typography>

        {!hasFilters && !isMobile && (
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={onCreateNote}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
            }}
          >
            Create your first note
          </Button>
        )}
      </Box>
    </Fade>
  );
};

// Pull to Refresh Indicator
const PullToRefreshIndicator = ({ isPulling, pullProgress }) => {
  const theme = useTheme();

  if (!isPulling && pullProgress === 0) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        py: 2,
        transform: `translateY(${Math.min(pullProgress * 60, 60) - 60}px)`,
        opacity: pullProgress,
        transition: isPulling ? 'none' : 'all 0.3s ease',
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: 'background.paper',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `rotate(${pullProgress * 360}deg)`,
          transition: isPulling ? 'none' : 'transform 0.3s ease',
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
      </Box>
    </Box>
  );
};

// Mobile Note Detail Sheet
// eslint-disable-next-line max-lines-per-function
const MobileNoteSheet = ({ note, open, onClose, onEdit, onDelete, onPermissionChange, canEdit, isSystemAdmin, entityId, loadNotes }) => {
  const theme = useTheme();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!note) return null;

  // Get title - prefer explicit title, then first tag, then fallback
  const noteTitle = note.title
    || (note.tags && note.tags.length > 0
      ? note.tags[0].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : 'Untitled Note');

  const getVisibilityInfo = () => {
    switch (note.visibility) {
      case 'private':
        return { color: theme.palette.secondary.main, icon: <LockIcon sx={{ fontSize: 14 }} />, label: 'Private' };
      case 'public':
        return { color: '#23A38F', icon: <PublicIcon sx={{ fontSize: 14 }} />, label: 'Public' };
      case 'role-based':
        return { color: '#D8A200', icon: <GroupIcon sx={{ fontSize: 14 }} />, label: 'Team' };
      default:
        return { color: theme.palette.grey[500], icon: null, label: note.visibility };
    }
  };

  const visibilityInfo = getVisibilityInfo();

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '90vh',
          bgcolor: '#FAFAFA',
        },
      }}
    >
      {/* Drag Handle */}
      <Box
        sx={{
          width: 40,
          height: 5,
          bgcolor: '#E0E0E0',
          borderRadius: 3,
          mx: 'auto',
          mt: 1.5,
          mb: 1,
        }}
      />

      {/* Clean Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: '#EEEEEE',
          bgcolor: '#FAFAFA',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <IconButton
              onClick={() => { onEdit(note); onClose(); }}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
              }}
            >
              <EditIcon sx={{ fontSize: 22 }} />
            </IconButton>
          )}
          <IconButton
            onClick={() => onPermissionChange(note)}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <ShareIcon sx={{ fontSize: 22 }} />
          </IconButton>
          {canEdit && (isSystemAdmin || !note.isImmutable) && (
            <IconButton
              onClick={() => setShowDeleteConfirm(true)}
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: 'error.main' }
              }}
            >
              <MoreVertIcon sx={{ fontSize: 22 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {/* Title Section */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1.5rem',
              lineHeight: 1.3,
              mb: 1.5,
            }}
          >
            {noteTitle}
          </Typography>

          {/* Meta Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
            >
              {format(parseDate(note.createdAt), 'MMM d, yyyy • h:mm a')}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: visibilityInfo.color,
              }}
            >
              {visibilityInfo.icon}
              <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                {visibilityInfo.label}
              </Typography>
            </Box>

            {note.isImmutable && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.disabled' }}>
                <LockIcon sx={{ fontSize: 14 }} />
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  Locked
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Tags */}
        {note.tags && note.tags.length > 0 && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {note.tags.map((tag, idx) => (
                <Chip
                  key={idx}
                  label={tag}
                  size="small"
                  sx={{
                    height: 28,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    border: 'none',
                    '& .MuiChip-label': { px: 1.5 },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Audio Player (if note has audio) */}
        <Box sx={{ px: 3 }}>
          <NoteAudioPlayer note={note} entityId={entityId} />
        </Box>

        {/* Note Content */}
        <Box sx={{ px: 3, pb: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              bgcolor: '#FFFFFF',
              borderRadius: 3,
              border: '1px solid',
              borderColor: '#EEEEEE',
              minHeight: 120,
            }}
          >
            <NotebookEditor content={note.content} editable={false} placeholder="" />
          </Paper>
        </Box>

        {/* Attachments Section */}
        {note.id && (
          <Box sx={{ px: 3, pb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                color: 'text.secondary',
              }}
            >
              <AttachFileIcon sx={{ fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Attachments
              </Typography>
            </Box>
            <AttachmentsList noteId={note.id} entityId={entityId} onDelete={loadNotes} />
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Menu */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        PaperProps={{
          sx: { borderRadius: 3, maxWidth: 320 }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <DeleteIcon sx={{ fontSize: 28, color: 'error.main' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Delete Note?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: 'center' }}>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.primary',
              borderColor: 'divider',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onDelete(note);
              setShowDeleteConfirm(false);
              onClose();
            }}
            variant="contained"
            color="error"
            sx={{
              flex: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </SwipeableDrawer>
  );
};

// Loading Skeleton for Mobile
const MobileLoadingSkeleton = () => (
  <Box sx={{ px: 2 }}>
    {[1, 2, 3, 4].map((i) => (
      <Box
        key={i}
        sx={{
          mb: 1.5,
          p: 2,
          borderRadius: 4,
          bgcolor: 'background.paper',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Skeleton variant="text" width={60} height={16} />
          <Skeleton variant="circular" width={8} height={8} />
        </Box>
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="100%" height={18} />
        <Skeleton variant="text" width="80%" height={18} sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" width={48} height={24} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    ))}
  </Box>
);

// Main Notebook Component
// eslint-disable-next-line max-lines-per-function
const Notebook = ({ entityId, entityName, autoUpload = false }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTags, setFilterTags] = useState([]);
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);

  // Mobile-specific state
  const [showFilters, setShowFilters] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [mobileViewNote, setMobileViewNote] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullProgress, setPullProgress] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const pullStartY = useRef(0);
  const contentRef = useRef(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewingNote, setViewingNote] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [permissionDialogNote, setPermissionDialogNote] = useState(null);
  const [deleteConfirmNote, setDeleteConfirmNote] = useState(null);
  const [publicConfirmDialog, setPublicConfirmDialog] = useState(false);
  const [publicConfirmText, setPublicConfirmText] = useState('');

  // Form states
  const [noteContent, setNoteContent] = useState('');
  const [noteTags, setNoteTags] = useState([]);
  const [noteVisibility, setNoteVisibility] = useState('private');
  const [noteAllowedRoles, setNoteAllowedRoles] = useState([]);
  const [noteEditableRoles, setNoteEditableRoles] = useState([]);
  const [noteIsImmutable, setNoteIsImmutable] = useState(false);
  const [userEntityRoles, setUserEntityRoles] = useState([]);

  // Helper function for visibility icon
  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'private':
        return <PersonIcon fontSize="small" />;
      case 'public':
        return <PublicIcon fontSize="small" />;
      case 'role-based':
        return <GroupIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Available roles for role-based visibility
  const AVAILABLE_ROLES = [
    { id: 'admin', label: 'Admin', description: 'Full entity access' },
    { id: 'reports', label: 'Reports', description: 'Generate and export reports' },
    { id: 'financial', label: 'Financial', description: 'Access to financial data' },
    { id: 'leadership', label: 'Leadership', description: 'Access to leadership data' },
    { id: 'personal', label: 'Personal', description: 'Access to HR/personal data' },
    { id: 'basic', label: 'Basic', description: 'Read-only access' }
  ];

  // Get all unique tags from notes
  const allTags = React.useMemo(() => {
    const tagSet = new Set();
    notes.forEach(note => {
      note.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [notes]);

  // Load notes + permissions in parallel
  const loadNotes = useCallback(async () => {
    if (!entityId) return;

    setLoading(true);
    setError(null);

    try {
      const [notesResult, rolesResult, permissionsResult] = await Promise.allSettled([
        api.listEntityNotes(entityId),
        api.getUserRolesByEmail(user.email, entityId),
        api.getEntityPermissions(entityId),
      ]);

      if (notesResult.status === 'fulfilled') {
        setNotes(notesResult.value.notes || []);
      } else {
        console.error('Failed to load notes:', notesResult.reason);
        setError('Failed to load notes');
        setLoading(false);
        return;
      }

      if (rolesResult.status === 'fulfilled') {
        setIsSystemAdmin(rolesResult.value.isPlatformAdmin || false);
      } else {
        console.warn('Failed to load user roles:', rolesResult.reason);
      }

      if (permissionsResult.status === 'fulfilled') {
        const userPermission = permissionsResult.value?.permissions?.[user.uid];
        setUserEntityRoles(userPermission?.roles || []);
      } else {
        console.warn('Failed to load entity permissions:', permissionsResult.reason);
      }
    } catch (error) {
      console.error('Unexpected error in loadNotes:', error);
      setError('Failed to load notes');
    }

    setLoading(false);
  }, [entityId, user.email, user.uid]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Filter and sort notes
  const filteredNotes = React.useMemo(() => {
    let filtered = [...notes];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(note =>
        note.content?.toLowerCase().includes(search) ||
        note.tags?.some(tag => tag.toLowerCase().includes(search))
      );
    }

    // Tag filter
    if (filterTags.length > 0) {
      filtered = filtered.filter(note =>
        filterTags.every(tag => note.tags?.includes(tag))
      );
    }

    // Visibility filter
    if (filterVisibility !== 'all') {
      filtered = filtered.filter(note => note.visibility === filterVisibility);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return parseDate(b.createdAt) - parseDate(a.createdAt);
        case 'oldest':
          return parseDate(a.createdAt) - parseDate(b.createdAt);
        case 'updated':
          return parseDate(b.updatedAt) - parseDate(a.updatedAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [notes, searchTerm, filterTags, filterVisibility, sortBy]);

  // Create note
  const handleCreateNote = async () => {
    if (noteVisibility === 'public' && !publicConfirmDialog) {
      setPublicConfirmDialog(true);
      return;
    }

    if (noteVisibility === 'public' && publicConfirmText !== 'PUBLIC') {
      setError('Please type PUBLIC to confirm');
      return;
    }

    try {
      await api.createManualNote({
        entityId,
        content: noteContent,
        tags: noteTags,
        visibility: noteVisibility,
        allowedRoles: noteVisibility === 'role-based' ? noteAllowedRoles : undefined,
      });

      setCreateDialogOpen(false);
      resetForm();
      loadNotes();
    } catch (error) {
      console.error('Failed to create note:', error);
      setError('Failed to create note');
    }
  };

  // Update note
  const handleUpdateNote = async () => {
    if (!editingNote) return;

    try {
      await api.updateNoteContent({
        noteId: editingNote.id,
        entityId,
        content: noteContent,
        tags: noteTags,
      });

      setEditingNote(null);
      resetForm();
      loadNotes();
    } catch (error) {
      console.error('Failed to update note:', error);
      setError('Failed to update note');
    }
  };

  // Update permissions
  const handleUpdatePermissions = async () => {
    if (!permissionDialogNote) return;

    if (noteVisibility === 'public' && permissionDialogNote.visibility !== 'public' && !publicConfirmDialog) {
      setPublicConfirmDialog(true);
      return;
    }

    if (noteVisibility === 'public' && permissionDialogNote.visibility !== 'public' && publicConfirmText !== 'PUBLIC') {
      setError('Please type PUBLIC to confirm');
      return;
    }

    try {
      await api.updateNotePermissions({
        noteId: permissionDialogNote.id,
        entityId,
        visibility: noteVisibility,
        isImmutable: noteIsImmutable,
        allowedRoles: noteVisibility === 'role-based' ? noteAllowedRoles : undefined,
        editableByRoles: noteEditableRoles,
      });

      setPermissionDialogNote(null);
      resetForm();
      loadNotes();
    } catch (error) {
      console.error('Failed to update permissions:', error);
      setError('Failed to update permissions');
    }
  };

  // Delete note
  const handleDeleteNote = async () => {
    if (!deleteConfirmNote) return;

    try {
      await api.deleteNote(deleteConfirmNote.id, entityId);
      setDeleteConfirmNote(null);
      loadNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
      setError('Failed to delete note');
    }
  };

  const resetForm = () => {
    setNoteContent('');
    setNoteTags([]);
    setNoteVisibility('private');
    setNoteAllowedRoles([]);
    setNoteEditableRoles([]);
    setNoteIsImmutable(false);
    setPublicConfirmDialog(false);
    setPublicConfirmText('');
  };

  const canEditNote = (note) => {
    if (isSystemAdmin) return true;
    if (note.isImmutable && !isSystemAdmin) return false;
    if (note.createdBy === user.uid) return true;
    return note.editableByRoles?.some(role => userEntityRoles.includes(role));
  };

  // Count active filters
  const activeFilterCount = (filterTags.length > 0 ? 1 : 0) +
    (filterVisibility !== 'all' ? 1 : 0) +
    (sortBy !== 'newest' ? 1 : 0);

  // Pull to refresh handlers for mobile
  const handlePullStart = useCallback((e) => {
    if (!isMobile || !contentRef.current) return;
    if (contentRef.current.scrollTop === 0) {
      pullStartY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, [isMobile]);

  const handlePullMove = useCallback((e) => {
    if (!isPulling || !isMobile) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - pullStartY.current;
    if (diff > 0) {
      const progress = Math.min(diff / 100, 1);
      setPullProgress(progress);
    }
  }, [isPulling, isMobile]);

  const handlePullEnd = useCallback(async () => {
    if (!isMobile) return;
    setIsPulling(false);
    if (pullProgress >= 0.8) {
      setIsRefreshing(true);
      await loadNotes();
      setIsRefreshing(false);
    }
    setPullProgress(0);
  }, [isMobile, pullProgress, loadNotes]);

  return (
    <Box sx={{ p: isMobile ? 0 : 3, pb: isMobile ? 12 : 3, minHeight: '100vh', bgcolor: isMobile ? 'grey.50' : 'transparent' }}>
      {/* Refresh indicator */}
      {isRefreshing && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            height: 3,
          }}
        />
      )}
      {/* Mobile Header */}
      {isMobile ? (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            bgcolor: 'grey.50',
            pt: 1,
            pb: 1.5,
            px: 2,
          }}
        >
          {/* Title row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: 'text.primary',
                  fontSize: '1.75rem',
                  letterSpacing: -0.5,
                }}
              >
                Notes
              </Typography>
              {entityName && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    display: 'block',
                    mt: -0.25,
                  }}
                >
                  {entityName}
                </Typography>
              )}
            </Box>

            {/* Action icons - larger touch targets */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => setShowSearch(!showSearch)}
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: showSearch ? 'primary.main' : alpha(theme.palette.grey[200], 0.8),
                  color: showSearch ? 'white' : 'text.primary',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: showSearch ? 'primary.dark' : 'grey.300' },
                  '&:active': { transform: 'scale(0.95)' },
                }}
              >
                <SearchIcon />
              </IconButton>
              <IconButton
                onClick={() => setShowFilters(true)}
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: activeFilterCount > 0 ? 'primary.main' : alpha(theme.palette.grey[200], 0.8),
                  color: activeFilterCount > 0 ? 'white' : 'text.primary',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: activeFilterCount > 0 ? 'primary.dark' : 'grey.300' },
                  '&:active': { transform: 'scale(0.95)' },
                }}
              >
                <Badge
                  badgeContent={activeFilterCount}
                  color="secondary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.65rem',
                      height: 18,
                      minWidth: 18,
                    },
                  }}
                >
                  <TuneIcon />
                </Badge>
              </IconButton>
            </Box>
          </Box>

          {/* Search bar - collapsible with animation */}
          <Collapse in={showSearch} timeout={200}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={showSearch}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  border: '1px solid',
                  borderColor: 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'divider',
                  },
                  '&.Mui-focused': {
                    borderColor: 'primary.main',
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                  '& fieldset': { border: 'none' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      sx={{ color: 'text.secondary' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Collapse>

          {/* Notes count pill */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${filteredNotes.length} ${filteredNotes.length === 1 ? 'note' : 'notes'}`}
              size="small"
              sx={{
                height: 26,
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                border: 'none',
              }}
            />
            {activeFilterCount > 0 && (
              <Chip
                label="Filtered"
                size="small"
                onDelete={() => {
                  setFilterTags([]);
                  setFilterVisibility('all');
                  setSortBy('newest');
                }}
                sx={{
                  height: 26,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.secondary.main,
                    fontSize: 16,
                  },
                }}
              />
            )}
          </Box>
        </Box>
      ) : (
        /* Desktop Header */
        (<>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4">
              Entity Notebook
              {entityName && (
                <Typography variant="subtitle1" color="text.secondary">
                  {entityName}
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                disabled={!entityId}
              >
                New Note
              </Button>

              <FileUploadButton
                entityId={entityId}
                onUploadComplete={() => {
                  loadNotes();
                }}
                compact={false}
                autoTrigger={autoUpload}
              />

              <AudioRecorderButton
                entityId={entityId}
                entityName={entityName}
                onSave={() => {
                  loadNotes();
                }}
                compact={false}
              />
            </Box>
          </Box>
          {/* Desktop Search and Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Autocomplete
                  multiple
                  size="small"
                  options={allTags}
                  value={filterTags}
                  onChange={(e, newValue) => setFilterTags(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Filter by tags" placeholder="Select tags" />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={filterVisibility}
                    onChange={(e) => setFilterVisibility(e.target.value)}
                    label="Visibility"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="private">Private</MenuItem>
                    <MenuItem value="public">Public</MenuItem>
                    <MenuItem value="role-based">Role-based</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort by</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Sort by"
                  >
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="oldest">Oldest</MenuItem>
                    <MenuItem value="updated">Recently Updated</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </>)
      )}
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, mx: isMobile ? 2 : 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {/* Notes List */}
      <Box
        ref={contentRef}
        onTouchStart={handlePullStart}
        onTouchMove={handlePullMove}
        onTouchEnd={handlePullEnd}
        sx={{
          px: isMobile ? 2 : 0,
          position: 'relative',
          minHeight: isMobile ? 'calc(100vh - 200px)' : 'auto',
        }}
      >
        {/* Pull to refresh indicator */}
        {isMobile && <PullToRefreshIndicator isPulling={isPulling} pullProgress={pullProgress} />}

        {loading ? (
          // Loading skeleton
          (isMobile ? (<MobileLoadingSkeleton />) : (<Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid
                key={i}
                size={{
                  xs: 12,
                  md: 6,
                  lg: 4
                }}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="70%" height={28} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="80%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>))
        ) : filteredNotes.length === 0 ? (
          // Empty state
          (<EmptyState
            hasFilters={searchTerm || filterTags.length > 0 || filterVisibility !== 'all'}
            onCreateNote={() => setCreateDialogOpen(true)}
            isMobile={isMobile}
          />)
        ) : (
          // Notes list
          (isMobile ? (<Box sx={{ pt: 1 }}>
            {filteredNotes.map((note, index) => (
              <MobileNoteCard
                key={note.id}
                note={note}
                index={index}
                onView={(note) => setMobileViewNote(note)}
                onEdit={(note) => {
                  setEditingNote(note);
                  setNoteContent(note.content);
                  setNoteTags(note.tags || []);
                }}
                onDelete={(note) => setDeleteConfirmNote(note)}
                onPermissionChange={(note) => {
                  setPermissionDialogNote(note);
                  setNoteVisibility(note.visibility);
                  setNoteIsImmutable(note.isImmutable);
                  setNoteAllowedRoles(note.allowedRoles || []);
                  setNoteEditableRoles(note.editableByRoles || []);
                }}
                canEdit={canEditNote(note)}
                isSystemAdmin={isSystemAdmin}
              />
            ))}
            {/* Bottom spacing for FAB */}
            <Box sx={{ height: 100 }} />
          </Box>) : (<Grid container spacing={3}>
            {filteredNotes.map((note) => (
              <Grid
                key={note.id}
                size={{
                  xs: 12,
                  md: 6,
                  lg: 4
                }}>
                <DesktopNoteCard
                  note={note}
                  onView={(note) => setViewingNote(note)}
                  onEdit={(note) => {
                    setEditingNote(note);
                    setNoteContent(note.content);
                    setNoteTags(note.tags || []);
                  }}
                  onDelete={(note) => setDeleteConfirmNote(note)}
                  onPermissionChange={(note) => {
                    setPermissionDialogNote(note);
                    setNoteVisibility(note.visibility);
                    setNoteIsImmutable(note.isImmutable);
                    setNoteAllowedRoles(note.allowedRoles || []);
                    setNoteEditableRoles(note.editableByRoles || []);
                  }}
                  canEdit={canEditNote(note)}
                  isSystemAdmin={isSystemAdmin}
                />
              </Grid>
            ))}
          </Grid>))
        )}
      </Box>
      {/* Mobile Note Detail Sheet */}
      {isMobile && (
        <MobileNoteSheet
          note={mobileViewNote}
          open={!!mobileViewNote}
          onClose={() => setMobileViewNote(null)}
          onEdit={(note) => {
            setEditingNote(note);
            setNoteContent(note.content);
            setNoteTags(note.tags || []);
          }}
          onDelete={(note) => setDeleteConfirmNote(note)}
          onPermissionChange={(note) => {
            setPermissionDialogNote(note);
            setNoteVisibility(note.visibility);
            setNoteIsImmutable(note.isImmutable);
            setNoteAllowedRoles(note.allowedRoles || []);
            setNoteEditableRoles(note.editableByRoles || []);
          }}
          canEdit={canEditNote(mobileViewNote || {})}
          isSystemAdmin={isSystemAdmin}
          entityId={entityId}
          loadNotes={loadNotes}
        />
      )}
      {/* Mobile Filter Drawer - Enhanced Design */}
      <SwipeableDrawer
        anchor="bottom"
        open={showFilters}
        onClose={() => setShowFilters(false)}
        onOpen={() => setShowFilters(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '75vh',
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 3, pb: 4 }}>
          {/* Drawer handle */}
          <Box
            sx={{
              width: 36,
              height: 4,
              bgcolor: 'grey.300',
              borderRadius: 2,
              mx: 'auto',
              mb: 2.5,
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
              Sort & Filter
            </Typography>
            {activeFilterCount > 0 && (
              <Button
                size="small"
                onClick={() => {
                  setFilterTags([]);
                  setFilterVisibility('all');
                  setSortBy('newest');
                }}
                sx={{
                  color: theme.palette.secondary.main,
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Reset all
              </Button>
            )}
          </Box>

          {/* Sort */}
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              letterSpacing: 1,
              fontSize: '0.7rem',
              display: 'block',
              mb: 1.5,
            }}
          >
            Sort by
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3.5, flexWrap: 'wrap' }}>
            {[
              { value: 'newest', label: 'Newest first' },
              { value: 'oldest', label: 'Oldest first' },
              { value: 'updated', label: 'Recently updated' },
            ].map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                onClick={() => setSortBy(option.value)}
                sx={{
                  height: 36,
                  fontSize: '0.875rem',
                  fontWeight: sortBy === option.value ? 600 : 400,
                  bgcolor: sortBy === option.value
                    ? theme.palette.primary.main
                    : alpha(theme.palette.grey[200], 0.8),
                  color: sortBy === option.value ? 'white' : 'text.primary',
                  border: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: sortBy === option.value
                      ? theme.palette.primary.dark
                      : theme.palette.grey[300],
                  },
                  '&:active': { transform: 'scale(0.97)' },
                }}
              />
            ))}
          </Box>

          {/* Visibility */}
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              letterSpacing: 1,
              fontSize: '0.7rem',
              display: 'block',
              mb: 1.5,
            }}
          >
            Visibility
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3.5, flexWrap: 'wrap' }}>
            {[
              { value: 'all', label: 'All notes' },
              { value: 'private', label: 'Private', color: theme.palette.secondary.main },
              { value: 'public', label: 'Public', color: '#23A38F' },
              { value: 'role-based', label: 'Role-based', color: '#D8A200' },
            ].map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                onClick={() => setFilterVisibility(option.value)}
                sx={{
                  height: 36,
                  fontSize: '0.875rem',
                  fontWeight: filterVisibility === option.value ? 600 : 400,
                  bgcolor: filterVisibility === option.value
                    ? (option.color || theme.palette.primary.main)
                    : alpha(theme.palette.grey[200], 0.8),
                  color: filterVisibility === option.value ? 'white' : 'text.primary',
                  border: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: filterVisibility === option.value
                      ? (option.color || theme.palette.primary.dark)
                      : theme.palette.grey[300],
                  },
                  '&:active': { transform: 'scale(0.97)' },
                }}
              />
            ))}
          </Box>

          {/* Tags */}
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              letterSpacing: 1,
              fontSize: '0.7rem',
              display: 'block',
              mb: 1.5,
            }}
          >
            Filter by tags
          </Typography>
          <Autocomplete
            multiple
            size="medium"
            options={allTags}
            value={filterTags}
            onChange={(e, newValue) => setFilterTags(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={filterTags.length === 0 ? "Select tags..." : ""}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.grey[100], 0.5),
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: 'divider' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    '& .MuiChip-deleteIcon': {
                      color: alpha(theme.palette.primary.main, 0.6),
                      '&:hover': { color: theme.palette.primary.main },
                    },
                  }}
                />
              ))
            }
            sx={{ mb: 4 }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={() => setShowFilters(false)}
            sx={{
              borderRadius: 3,
              py: 1.75,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            }}
          >
            Show {filteredNotes.length} {filteredNotes.length === 1 ? 'note' : 'notes'}
          </Button>
        </Box>
      </SwipeableDrawer>
      {/* Mobile FAB - Clean single button or SpeedDial */}
      {isMobile && (
        <>
          <Zoom in timeout={300}>
            <SpeedDial
              ariaLabel="Create note"
              sx={{
                position: 'fixed',
                bottom: 100, // Above bottom nav (80px) + padding
                right: 20,
                '& .MuiSpeedDial-fab': {
                  width: 60,
                  height: 60,
                  bgcolor: theme.palette.secondary.main,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.4)}`,
                  '&:hover': {
                    bgcolor: theme.palette.secondary.dark,
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                },
                '& .MuiSpeedDialAction-fab': {
                  bgcolor: 'background.paper',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    bgcolor: 'background.paper',
                  },
                },
              }}
              icon={
                <SpeedDialIcon
                  icon={<AddIcon sx={{ fontSize: 28 }} />}
                  openIcon={<CloseIcon sx={{ fontSize: 24 }} />}
                />
              }
              onClose={() => setSpeedDialOpen(false)}
              onOpen={() => setSpeedDialOpen(true)}
              open={speedDialOpen}
            >
              <SpeedDialAction
                icon={<MicIcon sx={{ color: theme.palette.primary.main }} />}
                tooltipTitle="Record audio"
                tooltipOpen
                tooltipPlacement="left"
                onClick={() => {
                  setSpeedDialOpen(false);
                  document.querySelector('[data-testid="audio-recorder-button"]')?.click();
                }}
                FabProps={{
                  sx: {
                    width: 48,
                    height: 48,
                  },
                }}
              />
              <SpeedDialAction
                icon={<UploadIcon sx={{ color: theme.palette.primary.main }} />}
                tooltipTitle="Upload file"
                tooltipOpen
                tooltipPlacement="left"
                onClick={() => {
                  setSpeedDialOpen(false);
                  document.querySelector('[data-testid="file-upload-button"]')?.click();
                }}
                FabProps={{
                  sx: {
                    width: 48,
                    height: 48,
                  },
                }}
              />
              <SpeedDialAction
                icon={<NoteAddIcon sx={{ color: theme.palette.primary.main }} />}
                tooltipTitle="New note"
                tooltipOpen
                tooltipPlacement="left"
                onClick={() => {
                  setSpeedDialOpen(false);
                  setCreateDialogOpen(true);
                }}
                FabProps={{
                  sx: {
                    width: 48,
                    height: 48,
                  },
                }}
              />
            </SpeedDial>
          </Zoom>

          {/* Hidden buttons for SpeedDial triggers */}
          <Box sx={{ display: 'none' }}>
            <FileUploadButton
              entityId={entityId}
              onUploadComplete={() => {
                loadNotes();
              }}
              compact={true}
              data-testid="file-upload-button"
            />
            <AudioRecorderButton
              entityId={entityId}
              entityName={entityName}
              onSave={() => {
                loadNotes();
              }}
              compact={true}
              data-testid="audio-recorder-button"
            />
          </Box>
        </>
      )}
      {/* View Note Dialog (Read-Only) - Desktop only, mobile uses sheet */}
      {!isMobile && (
        <Dialog
          open={!!viewingNote}
          onClose={() => setViewingNote(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={600}>View Note</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={getVisibilityIcon(viewingNote?.visibility)}
                  label={viewingNote?.visibility}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
                <IconButton onClick={() => setViewingNote(null)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {viewingNote && (
              <Box sx={{ mt: 1 }}>
                {viewingNote.tags && viewingNote.tags.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {viewingNote.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          mr: 1,
                          mb: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.main,
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Audio Player (if note has audio) */}
                <NoteAudioPlayer note={viewingNote} entityId={entityId} />

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Created: {format(parseDate(viewingNote.createdAt), 'MMM d, yyyy h:mm a')}
                  {viewingNote.createdBy && ` by ${viewingNote.createdBy}`}
                </Typography>

                <Paper elevation={0} sx={{ p: 2.5, bgcolor: alpha(theme.palette.grey[100], 0.5), borderRadius: 3 }}>
                  <NotebookEditor
                    content={viewingNote.content}
                    editable={false}
                    placeholder=""
                  />
                </Paper>

                {/* Attachments Section */}
                {viewingNote.id && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachFileIcon fontSize="small" />
                      Attachments
                    </Typography>
                    <AttachmentsList
                      noteId={viewingNote.id}
                      entityId={entityId}
                      onDelete={() => loadNotes()}
                    />
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setViewingNote(null)} sx={{ borderRadius: 2 }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* Create/Edit Dialog - Full screen on mobile */}
      <Dialog
        open={createDialogOpen || !!editingNote}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditingNote(null);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        slots={{ transition: isMobile ? Slide : undefined }}
        slotProps={{ transition: isMobile ? { direction: 'up' } : undefined }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
          },
        }}
      >
        {isMobile ? (
          // Mobile full-screen layout
          (<>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <IconButton
                onClick={() => {
                  setCreateDialogOpen(false);
                  setEditingNote(null);
                  resetForm();
                }}
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="subtitle1" fontWeight={600}>
                {editingNote ? 'Edit Note' : 'New Note'}
              </Typography>
              <Button
                onClick={editingNote ? handleUpdateNote : handleCreateNote}
                disabled={!noteContent.trim()}
                sx={{
                  fontWeight: 600,
                  color: noteContent.trim() ? 'secondary.main' : 'text.disabled',
                }}
              >
                {editingNote ? 'Save' : 'Create'}
              </Button>
            </Box>
            <DialogContent sx={{ p: 2, pb: 4 }}>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={allTags}
                value={noteTags}
                onChange={(e, newValue) => setNoteTags(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Add tags..."
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    />
                  ))
                }
              />

              {!editingNote && (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Visibility</InputLabel>
                    <Select
                      value={noteVisibility}
                      onChange={(e) => setNoteVisibility(e.target.value)}
                      label="Visibility"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="private">Private</MenuItem>
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="role-based">Role-based</MenuItem>
                    </Select>
                  </FormControl>

                  {noteVisibility === 'role-based' && (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Allowed Roles</InputLabel>
                      <Select
                        multiple
                        value={noteAllowedRoles}
                        onChange={(e) => setNoteAllowedRoles(e.target.value)}
                        label="Allowed Roles"
                        sx={{ borderRadius: 2 }}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={AVAILABLE_ROLES.find(r => r.id === value)?.label || value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {AVAILABLE_ROLES.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            <ListItemText primary={role.label} secondary={role.description} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}

              <NotebookEditor
                content={noteContent}
                onChange={setNoteContent}
                placeholder="Start writing..."
              />
            </DialogContent>
          </>)
        ) : (
          // Desktop layout
          (<>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                  {editingNote ? 'Edit Note' : 'Create New Note'}
                </Typography>
                <IconButton
                  onClick={() => {
                    setCreateDialogOpen(false);
                    setEditingNote(null);
                    resetForm();
                  }}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={allTags}
                  value={noteTags}
                  onChange={(e, newValue) => setNoteTags(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Add tags..."
                      helperText="Press Enter to add custom tags"
                      sx={{ mb: 3 }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      />
                    ))
                  }
                />

                {!editingNote && (
                  <>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Visibility</InputLabel>
                      <Select
                        value={noteVisibility}
                        onChange={(e) => setNoteVisibility(e.target.value)}
                        label="Visibility"
                      >
                        <MenuItem value="private">Private (Only you and system admins)</MenuItem>
                        <MenuItem value="public">Public (All entity members)</MenuItem>
                        <MenuItem value="role-based">Role-based</MenuItem>
                      </Select>
                    </FormControl>

                    {noteVisibility === 'role-based' && (
                      <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Allowed Roles</InputLabel>
                        <Select
                          multiple
                          value={noteAllowedRoles}
                          onChange={(e) => setNoteAllowedRoles(e.target.value)}
                          label="Allowed Roles"
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={AVAILABLE_ROLES.find(r => r.id === value)?.label || value} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          {AVAILABLE_ROLES.map((role) => (
                            <MenuItem key={role.id} value={role.id}>
                              <ListItemText primary={role.label} secondary={role.description} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}

                <NotebookEditor
                  content={noteContent}
                  onChange={setNoteContent}
                  placeholder="Write your note here..."
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button
                onClick={() => {
                  setCreateDialogOpen(false);
                  setEditingNote(null);
                  resetForm();
                }}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                onClick={editingNote ? handleUpdateNote : handleCreateNote}
                variant="contained"
                disabled={!noteContent.trim()}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {editingNote ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </>)
        )}
      </Dialog>
      {/* Permission Dialog */}
      <Dialog
        open={!!permissionDialogNote}
        onClose={() => {
          setPermissionDialogNote(null);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Note Permissions</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={noteVisibility}
                onChange={(e) => setNoteVisibility(e.target.value)}
                label="Visibility"
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="role-based">Role-based</MenuItem>
              </Select>
            </FormControl>

            {noteVisibility === 'role-based' && (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Allowed Roles (View)</InputLabel>
                  <Select
                    multiple
                    value={noteAllowedRoles}
                    onChange={(e) => setNoteAllowedRoles(e.target.value)}
                    label="Allowed Roles (View)"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={AVAILABLE_ROLES.find(r => r.id === value)?.label || value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {AVAILABLE_ROLES.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        <ListItemText primary={role.label} secondary={role.description} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Editable By Roles</InputLabel>
                  <Select
                    multiple
                    value={noteEditableRoles}
                    onChange={(e) => setNoteEditableRoles(e.target.value)}
                    label="Editable By Roles"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={AVAILABLE_ROLES.find(r => r.id === value)?.label || value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {AVAILABLE_ROLES.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        <ListItemText primary={role.label} secondary={role.description} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={noteIsImmutable}
                  onChange={(e) => setNoteIsImmutable(e.target.checked)}
                />
              }
              label="Make note immutable (cannot be edited)"
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPermissionDialogNote(null);
            resetForm();
          }}>
            Cancel
          </Button>
          <Button onClick={handleUpdatePermissions} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
      {/* Public Confirmation Dialog */}
      <Dialog
        open={publicConfirmDialog}
        onClose={() => {
          setPublicConfirmDialog(false);
          setPublicConfirmText('');
        }}
      >
        <DialogTitle>Confirm Public Visibility</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Making this note public will allow ALL entity members to view it.
            This action cannot be easily undone.
          </Alert>
          <TextField
            fullWidth
            label="Type PUBLIC to confirm"
            value={publicConfirmText}
            onChange={(e) => setPublicConfirmText(e.target.value)}
            helperText="This is to ensure you understand the implications"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPublicConfirmDialog(false);
            setPublicConfirmText('');
            setNoteVisibility('private');
          }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (editingNote) {
                handleUpdateNote();
              } else if (permissionDialogNote) {
                handleUpdatePermissions();
              } else {
                handleCreateNote();
              }
            }}
            variant="contained"
            color="warning"
            disabled={publicConfirmText !== 'PUBLIC'}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation - Enhanced for mobile */}
      <Dialog
        open={!!deleteConfirmNote}
        onClose={() => setDeleteConfirmNote(null)}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 4 : 3,
            mx: isMobile ? 2 : 'auto',
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Delete Note?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography color="text.secondary">
            This action cannot be undone. The note and all its attachments will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth
            onClick={handleDeleteNote}
            color="error"
            variant="contained"
            sx={{
              borderRadius: 3,
              py: 1.25,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Delete Note
          </Button>
          <Button
            fullWidth
            onClick={() => setDeleteConfirmNote(null)}
            sx={{
              borderRadius: 3,
              py: 1.25,
              fontWeight: 600,
              textTransform: 'none',
              color: 'text.primary',
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notebook;